'use client';
import React, { ChangeEvent, useMemo } from 'react';
import { ChatRequestBody } from '~/lib/chat';
import { Config, formFieldsFromZodSchema } from '~/lib/zod-form';
import {
  type ChatInferenceRequest,
  ChatInferenceRequestSchema,
  type CompletionInferenceRequest,
  CompletionInferenceRequestSchema,
} from '~/services/inference/chat';
import {
  type ControlNetOptions,
  ControlNetOptionsSchema,
  type ImageGenerationOptions,
  ImageGenerationOptionsSchema,
  type ImageTransformationOptions,
  type ImageTransformationWithImageStrengthOptions,
  imageTransformationWithImageStrengthSchema,
  type ImageTransformationWithStepsOptions,
  imageTransformationWithStepsSchema,
  type MinimalImageTransformationOptions,
  minimalImageTransformationSchema,
  Sd3BaseOptionsSchema,
  Sd3ImageToImageOptionsSchema,
  Sd3TextToImageOptionsSchema,
  validResolutions,
} from '~/services/inference/image-generation';
import { Model } from '~/services/models.types';
import { InfoCircledIcon } from '@radix-ui/react-icons';
import { EasyTooltip } from '../easy-tooltip';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Slider } from '../ui/slider';
import { Tag, TagInput } from '../ui/tag-input';
import { FunctionCallingSpec } from './chat/FunctionCallingSpec';
import { useActiveTabState } from './request.context';

// Map through valid resolutions
// Since we want to display on the frontend, width and height as one input we create two objects
// mapWidthAndHeight - which provides a map for the expected height value based on the width chosen
const mapWidthAndHeight: Record<number, number> = {};
// displayValue - grabs the value to output to the user
const displayValue: Record<number | string, string> = {};

displayValue['none'] = 'none';
for (const { width, height } of validResolutions) {
  mapWidthAndHeight[width] = height;
  displayValue[width] = `${width} x ${height}`;
}

type RequestState =
  | ControlNetOptions
  | ImageGenerationOptions
  | MinimalImageTransformationOptions
  | ImageTransformationWithStepsOptions
  | ImageTransformationWithImageStrengthOptions
  | CompletionInferenceRequest
  | ChatInferenceRequest;

export const ConfigPane = ({ model }: { model: Model }) => {
  const { selectedTab, requestState: rState, setRequestState } = useActiveTabState();
  const requestState = rState as RequestState;
  const imageToImageState = requestState as ImageTransformationOptions;

  const uiSchema = useMemo<Config[]>(() => {
    switch (selectedTab) {
      case 'chat':
        // eslint-disable-next-line no-case-declarations
        const thisSchema = formFieldsFromZodSchema({
          schema: ChatInferenceRequestSchema,
          excludedFields: ['messages', 'n', 'tools', 'model', 'stream', 'logprobs'],
          overides: {
            max_tokens: {
              // @ts-expect-error need type guard forthis model type for the tab selected
              max: model.metadata.contextLength || 4000,
            },
          },
        });
        // manually adding tools to the schema because its a special UI component
        if (model.tags.includes('Function Calling')) {
          thisSchema.push({
            key: 'functionCalling',
            type: 'tools',
            value: [],
            label: 'Function Calling',
          });
        }
        return thisSchema;
      case 'completion':
        return formFieldsFromZodSchema({
          schema: CompletionInferenceRequestSchema,
          excludedFields: ['prompt', 'n', 'model', 'logprobs', 'stream'],
          defaults: model.generationDefaults,
          overides: {
            max_tokens: {
              // @ts-expect-error need type guard forthis model type for the tab selected
              max: model.metadata.contextLength || 4000,
            },
          },
        });
      case 'image':
        if (/^sd3(-turbo)?$/iu.test(model.id)) {
          return formFieldsFromZodSchema({
            schema: Sd3TextToImageOptionsSchema,
            excludedFields: ['prompt', 'negative_prompt', 'mode', 'model'],
            overides: { authorization: { label: 'Stability API Key' } },
            defaults: model.generationDefaults,
          });
        }

        return formFieldsFromZodSchema({
          schema: ImageGenerationOptionsSchema,
          excludedFields: ['prompt', 'negative_prompt', 'height'],
          defaults: model.generationDefaults,
        });
      case 'image-to-image':
        if (/^sd3(-turbo)?$/iu.test(model.id)) {
          return formFieldsFromZodSchema({
            schema: Sd3ImageToImageOptionsSchema,
            excludedFields: ['prompt', 'negative_prompt', 'mode', 'model'],
            overides: { authorization: { label: 'Stability API Key' } },
            defaults: model.generationDefaults,
          });
        }

        let schema;
        switch (imageToImageState.init_image_mode) {
          case 'IMAGE_STRENGTH':
            schema = imageTransformationWithImageStrengthSchema;
            break;
          case 'STEP_SCHEDULE':
            schema = imageTransformationWithStepsSchema;
            break;
          default:
            schema = minimalImageTransformationSchema;
            break;
        }

        return formFieldsFromZodSchema({
          schema,
          excludedFields: ['prompt', 'negative_prompt'],
          defaults: model.generationDefaults,
        });
      case 'controlNet':
        return formFieldsFromZodSchema({
          schema: ControlNetOptionsSchema,
          excludedFields: ['prompt'],
          defaults: model.generationDefaults,
        });
      default:
        throw new Error('Invalid playground tab');
    }
  }, [selectedTab, imageToImageState.init_image_mode]);

  // We're updating the value of width and height concurrently since we have one visual select display
  function handleWidthAndHeightOptions(key: string, value: number) {
    if (key === 'width') {
      setRequestState({ field: 'width', value });
      setRequestState({ field: 'height', value: mapWidthAndHeight[value] });
    } else if (key === 'height') {
      setRequestState({ field: 'width', value: mapWidthAndHeight[value] });
      setRequestState({ field: 'height', value });
    }
  }

  return (
    <div className="mt-2 flex flex-col space-y-4">
      {uiSchema.map((props, index: number) => {
        const { type, key, label, value, description, optional, nullable, nullish } = props;

        return (
          <div key={`config-${selectedTab}-${(label as string) || index}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-zinc-200">
                <span>{key === 'width' ? 'Image Size' : label}</span>
                {!!description && (
                  <span className="text-muted-foreground">
                    <EasyTooltip content={description} side="bottom">
                      <InfoCircledIcon />
                    </EasyTooltip>
                  </span>
                )}

                {label.length < 10 && key === 'width' ? (
                  <>
                    <span className="shadow-aceternity rounded-md bg-white px-1 py-0.5 text-xs dark:bg-zinc-700 dark:text-zinc-300">
                      width
                    </span>{' '}
                    <span className="shadow-aceternity rounded-md bg-white px-1 py-0.5 text-xs dark:bg-zinc-700 dark:text-zinc-300">
                      height
                    </span>
                  </>
                ) : (
                  <span className="shadow-aceternity rounded-md bg-white px-1 py-0.5 text-xs dark:bg-zinc-700 dark:text-zinc-300">
                    {String(key)}
                  </span>
                )}
              </div>
              {type === 'number' && (
                <div className="inline-block rounded-md bg-gray-200 px-4 py-0.5 text-xs text-neutral-700 dark:bg-zinc-700 dark:text-zinc-200">
                  {(requestState as any)[key]?.toLocaleString()}
                </div>
              )}
            </div>
            {type === 'number' && (
              <Slider
                id={`slider-${index + 1}`}
                value={[(requestState as any)[key]]}
                max={props.max}
                step={props.step}
                min={props.min}
                className="mt-4 w-full"
                onValueChange={(v) => {
                  if (Array.isArray(v)) {
                    setRequestState({ field: key, value: v[0] });
                  }
                }}
              />
            )}
            {type === 'select' && (
              <div className="mt-2">
                <Select
                  key={String(key)}
                  value={(requestState as any)[key] || value}
                  onValueChange={(v) => {
                    if (v === null || v === 'null') {
                      setRequestState({ field: key, value: undefined });
                      return;
                    }
                    if (key === 'width') {
                      handleWidthAndHeightOptions(key, Number(v));
                      return;
                    }
                    setRequestState({ field: key, value: v });
                  }}
                  defaultValue={(requestState as any)[key] || value || 'null'}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        props.placeholder ?? (optional || nullable || nullish) ? 'None' : 'Please select (required)'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {(optional || nullable || nullish || (!(requestState as any)[key] && !value)) && (
                        <SelectItem value="null">
                          {optional || nullable || nullish ? 'None' : 'Please select (required)'}
                        </SelectItem>
                      )}
                      {props.options?.map((option: any, idx: number) => (
                        <SelectItem key={`option-${index}-${idx}`} value={option}>
                          {key === 'width' ? displayValue[option] : option}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            )}
            {type === 'checkbox' && (
              <div className="mb-4 flex items-center space-x-2 pt-4 ">
                <Checkbox
                  id={`checkbox-${key as string}`}
                  checked={(requestState as any)[key]}
                  onCheckedChange={() => {
                    setRequestState({ field: key, value: !(requestState as any)[key] });
                  }}
                />
                <Label htmlFor={`checkbox-${key as string}`} className="space-x-2 text-gray-500 dark:text-zinc-200">
                  <span>{label}</span>
                  <span className="shadow-aceternity rounded-md bg-white px-1 py-0.5 text-xs dark:bg-zinc-700 dark:text-zinc-200">
                    {String(key)}
                  </span>
                </Label>
              </div>
            )}
            {type === 'tools' && (
              <div className="mb-4 flex flex-col space-x-2 ">
                <div className="mb-2 text-xs opacity-50 ">JsonSchema definitions sent to the model</div>

                {/* @FIXME: (IW) fix requestBody types */}
                <FunctionCallingSpec
                  requestBody={requestState as unknown as ChatRequestBody}
                  setRequestBody={setRequestState}
                />
              </div>
            )}
            {type === 'tags' && (
              <div>
                <TagInput
                  className="w-full"
                  placeholder="Enter a stop word"
                  tags={(requestState as any)[key]?.map((t: string) => ({ id: t, text: t })) || []}
                  addOnBlur
                  setTags={(newTags: Tag[]) => {
                    setRequestState({ field: key, value: newTags.map((t) => t.text) });
                  }}
                />
              </div>
            )}
            {type === 'text' && (
              <div className="mt-2 w-full">
                <Input
                  id={`text-${index + 1}`}
                  value={(requestState as any)[key]}
                  placeholder={props.placeholder}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setRequestState({ field: key, value: e.target.value });
                  }}
                  className={key === 'authorization' ? 'mask-me' : ''}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
